import { KeyboardDoubleArrowUp, CalendarToday, AccountTree } from '@mui/icons-material';
import { Card as MuiCard, CardContent, styled, Chip, Tooltip, Typography, Avatar, Grid } from '@mui/material';
import { titleize } from 'inflection';
import React from 'react';

import { type GetTasksQuery, TaskPriorityEnum, TaskStatusEnum } from '@/graphql/generated/graphql';
import DateTimeFormatter from '@/shared-components/DateTimeFormatter';

interface TaskCardProps {
  task: NonNullable<GetTasksQuery['getTasks']>[0];
  onClick: () => void;
}

const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'priority',
})<{ priority: string }>(({ priority }) => ({
  borderLeft: `4px solid ${getColorForPriority(priority)}`,
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  width: '100%',
  '&:hover': {
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
}));

const getColorForPriority = (priority: string) => {
  return priority === TaskPriorityEnum.ImportantAndUrgent
    ? '#a1142e'
    : priority === TaskPriorityEnum.Important
      ? '#f97316'
      : priority === TaskPriorityEnum.Urgent
        ? '#10b981'
        : '#64748b';
};

const getColorForStatus = (status: string) => {
  return status === TaskStatusEnum.Assigned
    ? '#10b981'
    : status === TaskStatusEnum.Blocked
      ? '#f97316'
      : status === TaskStatusEnum.Completed
        ? '#64748b'
        : status === TaskStatusEnum.Started
          ? '#64748b'
          : '#a1142e';
};

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick }) => {
  const assignee = task.assignee?.employee;

  return (
    <StyledCard variant="outlined" onClick={onClick} priority={task.priority} sx={{ marginX: 2, marginBottom: 1 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Grid container alignItems="center" gap={1}>
          <KeyboardDoubleArrowUp sx={{ color: getColorForPriority(task.priority), fontSize: 20 }} />
          <Typography sx={{ fontWeight: 500, color: '#0f3559' }}>{task.title}</Typography>
        </Grid>

        {/* Tags Row */}
        <Grid container alignItems="center" gap={1} mb={2} mt={1}>
          <Chip
            size="small"
            label={titleize(task.priority)}
            sx={{
              backgroundColor: getColorForPriority(task.priority),
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
          <Chip
            size="small"
            label={titleize(task.status)}
            sx={{
              backgroundColor: getColorForStatus(task.status),
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
        </Grid>

        {/* Description Body */}
        <Typography
          variant="body2"
          color="textPrimary"
          sx={{
            fontSize: '0.85rem',
            mb: 2,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {task.description}
        </Typography>

        {/* Footer Row */}
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid container alignItems="center" gap={2} sx={{ color: '#64748b' }}>
            <Grid container alignItems="center" gap={0.5}>
              <AccountTree sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                {task.entityName}
              </Typography>
            </Grid>
            <Grid container alignItems="center" gap={0.5}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                <DateTimeFormatter rawDate={task.dueDate} />
              </Typography>
            </Grid>
          </Grid>
          <Tooltip
            key={`task-assignee-${task.id}`}
            title={`${assignee?.firstName} ${assignee?.lastName}`.trim() || 'Assignee'}
          >
            <Avatar
              alt={assignee ? `${assignee.firstName} ${assignee.lastName}`.trim() || 'Assignee' : 'Assignee'}
              src={assignee?.imageUrl as string}
              sx={{ fontSize: 14 }}
            >
              {assignee ? `${assignee.firstName?.charAt(0)}${assignee.lastName?.charAt(0)}` || 'A' : 'A'}
            </Avatar>
          </Tooltip>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default TaskCard;
